/** SUNTRONIX ONE — Shared Preflight block dialog.
 *
 *  Renders any `{error: "preflight_blocked", failed_checks: [...], counts: {...}}`
 *  payload as an actionable card list. Consumed by `usePreflightGuard` so
 *  every page (Shipments, Orders, Deployments, Recoveries, Invoices …)
 *  presents the same clarity when Preflight refuses a transaction.
 */
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldAlert, ChevronRight, ExternalLink, ClipboardCheck,
  FileSignature, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORY_TONE = {
  commercial:        "bg-amber-50 border-amber-200 text-amber-900",
  address_logistics: "bg-red-50 border-red-200 text-red-900",
  operational:       "bg-slate-50 border-slate-200 text-slate-900",
  data_quality:      "bg-violet-50 border-violet-200 text-violet-900",
  identity:          "bg-sky-50 border-sky-200 text-sky-900",
};

/** Rule → in-app route mapping so "Next action" becomes a one-tap link. */
const RULE_ROUTES = {
  contract_entitlement:      { to: "/commercial/contracts", label: "Open Contract Centre" },
  po_valid:                  { to: "/commercial",           label: "Manage POs" },
  customer_active:           { to: "/customers",            label: "Open customer" },
  no_critical_advisory:      { to: "/advisories",           label: "Open advisories" },
  no_blocking_exception:     { to: "/exceptions",           label: "Open exception queue" },
  no_dq_p0_p1:               { to: "/data-quality",         label: "Fix data-quality issues" },
  address_verified:          { to: "/customers",      label: "Verify address" },
  employee_active:           { to: "/customers",      label: "Open employee" },
  mdm_enrolled:              { to: "/assets",               label: "Open asset" },
  buffer_stock:              { to: "/warehouse",            label: "Open warehouse" },
  asset_state_ready:         { to: "/assets",               label: "Open asset" },
  customer_ownership_matches:{ to: "/customers",      label: "Verify ownership" },
  erasure_complete:          { to: "/inspection",           label: "Verify erasure" },
  integration_health:        { to: "/admin/integration-health",   label: "Integration health" },
};

/** Map a rule_id to a deep-link.  Falls back to any `/foo/bar` path
 *  detected inside the `next_action` string.  Returns null when no
 *  sensible target can be inferred so the button doesn't render. */
export function ruleActionLink(rule) {
  if (!rule) return null;
  const mapped = RULE_ROUTES[rule.rule_id];
  if (mapped) return mapped;
  const text = rule.next_action || "";
  const m = text.match(/(\/[a-z0-9][a-z0-9/-]*)/i);
  if (m) return { to: m[1], label: "Open referenced page" };
  return null;
}

export default function PreflightBlockDialog({
  block, onClose, onRetry, retryLabel = "Retry once resolved",
}) {
  const [action, setAction] = useState(null);      // { kind, rule } | null
  const [busy, setBusy] = useState(false);
  const [justification, setJustification] = useState("");
  if (!block) return null;
  const failed = Array.isArray(block.failed_checks) ? block.failed_checks : [];
  const counts = block.counts || {};

  const submitApprove = async () => {
    if (justification.trim().length < 10) {
      toast.error("Justification must be at least 10 characters"); return;
    }
    setBusy(true);
    try {
      await api.post("/preflight/approvals", {
        entity_kind: block.entity_kind,
        entity_id: block.entity_id,
        rule_id: action.rule.rule_id,
        justification: justification.trim(),
        valid_hours: 24,
      });
      toast.success(`Cleared: ${action.rule.name}. Click "Retry" to proceed.`);
      setAction(null); setJustification("");
      onRetry?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitQuickStub = async () => {
    if (justification.trim().length < 10) {
      toast.error("Reason must be at least 10 characters"); return;
    }
    setBusy(true);
    try {
      // block.master_company_id may not be included; the backend reads
      // it from the entity via preflight if needed, so we also pull it
      // from the block context if present.
      const mcId = block.master_company_id
        || action.rule?.master_company_id
        || block.customer?.master_company_id;
      const res = await api.post("/contract-centre/contracts/quick-stub", {
        master_company_id: mcId,
        service_code: block.service_code || "device_deployment",
        country: block.country || "IN",
        currency: block.currency || "INR",
        reason: justification.trim(),
      });
      toast.success(`Stub contract ${res.data.contract_number} created. Click "Retry".`);
      setAction(null); setJustification("");
      onRetry?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"
                            data-testid="preflight-block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-800">
            <ShieldAlert className="h-5 w-5" />
            Blocked by ONE Preflight™
          </DialogTitle>
          <div className="text-[12px] text-slate-500 mt-1">
            {block.summary || block.message || "Resolve every failed check before continuing."}
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 text-[11px]">
          {counts.pass != null &&
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Pass · {counts.pass}</span>}
          {counts.warning ?
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Warning · {counts.warning}</span> : null}
          {counts.approval_required ?
            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Approval · {counts.approval_required}</span> : null}
          {counts.blocking_failure ?
            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800">Blocked · {counts.blocking_failure}</span> : null}
        </div>

        <div className="space-y-2 mt-3">
          {failed.map((f, i) => (
            <div key={f.rule_id || i}
                    className={`rounded border p-3 ${CATEGORY_TONE[f.category] || "bg-slate-50 border-slate-200 text-slate-900"}`}
                    data-testid={`preflight-rule-${f.rule_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[13px] font-semibold">{f.name || f.rule_id}</div>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">{(f.category || "").replace(/_/g, " ")}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/70 border border-current/20">
                  {(f.blocking || f.result || "").replace(/_/g, " ")}
                </span>
              </div>
              {f.reason && (
                <div className="text-[12px] mt-2"><span className="opacity-70">Reason:</span> {String(f.reason).replace(/_/g, " ")}</div>
              )}
              {(f.current_value || f.required_value) && (
                <div className="text-[12px] mt-1 grid grid-cols-2 gap-2">
                  <div><span className="opacity-70">Current:</span> {stringify(f.current_value)}</div>
                  <div><span className="opacity-70">Required:</span> {stringify(f.required_value)}</div>
                </div>
              )}
              {f.source && (
                <div className="text-[12px] mt-1"><span className="opacity-70">Source:</span> {String(f.source)}</div>
              )}
              {f.next_action && (
                <div className="text-[12px] mt-2 flex items-start gap-1.5">
                  <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span><span className="font-semibold">Next action:</span> {String(f.next_action)}</span>
                </div>
              )}
              <div className="flex items-center flex-wrap gap-2 mt-2">
                {ruleActionLink(f) && (
                  <Link to={ruleActionLink(f).to}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-white/80 hover:bg-white border border-current/20 hover:border-current/40 font-medium"
                          data-testid={`preflight-fix-${f.rule_id}`}
                          onClick={() => onClose?.()}>
                    <ExternalLink className="h-3 w-3" /> {ruleActionLink(f).label}
                  </Link>
                )}
                {/* "Approve to clear" — only for soft (approval_required) rules. */}
                {f.result === "approval_required" && (
                  <button
                    onClick={() => setAction({ kind: "approve", rule: f })}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
                    data-testid={`preflight-approve-${f.rule_id}`}>
                    <ClipboardCheck className="h-3 w-3" /> Approve to clear
                  </button>
                )}
                {/* Contract-entitlement gap → 1-click stub contract shortcut. */}
                {f.rule_id === "contract_entitlement" && (
                  <button
                    onClick={() => setAction({ kind: "stub", rule: f })}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 font-medium"
                    data-testid={`preflight-stub-${f.rule_id}`}>
                    <FileSignature className="h-3 w-3" /> Log stub contract
                  </button>
                )}
                {f.owner_email && (
                  <a href={`mailto:${f.owner_email}`}
                        className="text-[11px] opacity-80 underline">
                    Escalate to {f.owner_email}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="preflight-close">Close</Button>
          {onRetry && (
            <Button onClick={onRetry} data-testid="preflight-retry">{retryLabel}</Button>
          )}
        </DialogFooter>

        {action && (
          <Dialog open onOpenChange={(v) => { if (!v) { setAction(null); setJustification(""); } }}>
            <DialogContent className="sm:max-w-md" data-testid="preflight-action-modal">
              <DialogHeader>
                <DialogTitle>
                  {action.kind === "approve"
                    ? `Approve · ${action.rule.name}`
                    : `Log stub contract · ${action.rule.name}`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-[12px] text-slate-600">
                  {action.kind === "approve"
                    ? "This clears the check for this entity for the next 24 hours. Every clearance is audit-logged with your email."
                    : "Creates a minimal DRAFT contract + entitlement so ops can proceed while commercial finalises the paperwork. The stub is auto-flagged for cleanup."}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    {action.kind === "approve" ? "Justification" : "Reason"}
                  </label>
                  <Textarea rows={3} value={justification}
                             onChange={(e) => setJustification(e.target.value)}
                             data-testid="preflight-justification"
                             placeholder="e.g. Contract in final review with Legal, dispatch cannot wait — approved by Commercial Head." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAction(null); setJustification(""); }}
                        data-testid="preflight-action-cancel">Cancel</Button>
                <Button onClick={action.kind === "approve" ? submitApprove : submitQuickStub}
                        disabled={busy} className="bg-slate-900"
                        data-testid="preflight-action-submit">
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                  {action.kind === "approve" ? "Approve" : "Create stub"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

function stringify(v) {
  if (v == null) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

/** Best-effort ledger log — never throws, never blocks the UI. */
export function logPreflightLedger(kind, payload) {
  try {
    api.post("/preflight-ledger", {
      kind,   // "block" | "retry" | "retry_resolved" | "retry_blocked_again"
      entity_kind: payload?.entity_kind,
      entity_id: payload?.entity_id,
      action: payload?.action,
      failed_checks: (payload?.failed_checks || []).map((f) => ({
        rule_id: f.rule_id, name: f.name, category: f.category,
        reason: f.reason, blocking: f.blocking,
        current_value: typeof f.current_value === "string" ? f.current_value : undefined,
      })),
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}
